<template>
  <div class="c-underlying-chart">
    <div class="chart-bar">
      <div v-if="Object.keys(underlyingData).length > 0" class="chart-info-bar">
        <div v-if="chartTypeValue === 'line' || chartTypeValue === 'area'" class="price-legend">
          <span>{{ underlyingData['q.RIC'].split('.')[0] }}</span>
          <span class="mr-1">{{ `, ${$t(`white-label.widgets.optionChain.underlyingChart.legend.line.price`)}` }}</span>
          <v-fidlet :data="underlyingData['q._TRDPRC_1'] || ''" type="float.price" />
          <span>{{ `[${underlyingData['q._CURRENCY']}]` }}</span>
          <v-flag :countryCode="underlyingData['q._COUNTRY']" class="ml-1 mr-1">
            <slot>{{ $t('white-label.countries.' + underlyingData['q._COUNTRY'].toUpperCase()) }}</slot>
          </v-flag>
          <span>{{ getFormattedTimespan() }}</span>
        </div>
        <div v-if="chartTypeValue === 'hviv'" class="volatility-legend">
          <span class="hv-indicator mr-1"></span>
          <span class="mr-3">{{ $t(`white-label.widgets.optionChain.underlyingChart.legend.hviv.hv`) }}</span>
          <span class="iv-indicator mr-1"></span>
          <span>{{ $t(`white-label.widgets.optionChain.underlyingChart.legend.hviv.iv`) }}</span>
        </div>
      </div>

      <div class="chart-controls-bar">
        <v-form-group
          :label="$t(`white-label.widgets.optionChain.underlyingChart.chartType.label`)"
          label-cols="auto"
          class="mr-3"
        >
          <v-form-select
            :value="chartTypeValue"
            :options="chartTypeOptions"
            @input="onUpdateChartType"
            :aria-label="$t(`white-label.widgets.optionChain.underlyingChart.chartType.label`)"
          />
        </v-form-group>
        <v-form-radio-group
          class="ml-3"
          name="timespan"
          :disabled="!timeseries.intradayData && !timeseries.historicalData"
          buttons
          button-variant="primary"
          :checked="chartTimespan"
          :options="timespanOptions"
          @input="onUpdateTimespan"
        >
        </v-form-radio-group>
      </div>
    </div>

    <v-stock-chart class="chart" :options="chartOptions" :api="chart" :height="130" ref="chart" />
  </div>
</template>

<script>
import VStockChart from '@base/VStockChart';

import moment from 'moment';

import { Volatility } from '@/utils/optionChainServices/calculationService';

export default {
  name: 'c-underlying-chart',
  components: {
    VStockChart
  },
  props: {
    timeseries: {
      type: Object,
      required: true
    },
    underlyingData: {
      type: Object,
      required: true
    },
    chartType: {
      type: String
    },
    chartTimespan: {
      type: String
    }
  },
  data() {
    return {
      chart: {},
      impliedVolatilityData: null,
      historicalVolatilityData: null,
      fromDate: null,
      chartDescription: null,
      historicalDataSplit: null,
      historicalChartData: null,
      intradayChartData: null,
      rangeDescriptionXAxis: ''
    };
  },
  watch: {
    timeseries: {
      handler: 'handleTimeseries',
      deep: true,
      immediate: false
    },
    chartTypeValue: {
      handler: 'handleChartType'
    },
    chartTimespan: {
      handler: 'handleChartTimespan'
    }
  },
  computed: {
    chartOptions() {
      return {
        lang: {
          accessibility: {
            chartContainerLabel: this.getChartTitle(),
            svgContainerLabel: this.getChartTitle(),
            defaultChartTitle: this.getChartTitle()
          }
        },
        chart: {
          zoomType: 'xy',
          marginTop: 5,
          marginRight: 40,
          marginBottom: 28,
          marginLeft: 1,
          padding: 0
        },
        navigator: {
          enabled: false
        },
        scrollbar: {
          enabled: false
        },
        rangeSelector: {
          enabled: false
        },
        legend: {
          enabled: false
        },
        xAxis: {
          labels: {
            autoRotation: false
          },
          accessibility: {
            rangeDescription: this.rangeDescriptionXAxis
          }
        },
        yAxis: {
          labels: {
            formatter: params => this.$f(params.value, 'float.price'),
            x: 40
            //format: '{value:.2f}'
          },
          startOnTick: false,
          endOnTick: false,
          tickWidth: 1,
          minPadding: 0
        },
        plotOptions: {
          series: {
            animation: false,
            turboThreshold: Infinity
          }
        },
        series: [this.seriesUnderlyingPrice, this.seriesImpliedVolatility, this.seriesHistoricalVolatility]
      };
    },
    chartTypeValue() {
      return this.chartType;
    },
    chartTimespanValue() {
      return this.chartTimespan;
    },
    chartTypeOptions() {
      return [
        { value: 'line', text: this.$t(`white-label.widgets.optionChain.underlyingChart.chartType.line`) },
        { value: 'area', text: this.$t(`white-label.widgets.optionChain.underlyingChart.chartType.area`) },
        { value: 'hviv', text: this.$t(`white-label.widgets.optionChain.underlyingChart.chartType.hviv`) }
      ];
    },
    seriesUnderlyingPrice() {
      return {
        id: 'underlyingPrice',
        name: this.$t('white-label.widgets.optionChain.underlyingChart.series.underlyingPrice'),
        type: this.chartTypeValue === 'line' ? 'line' : 'area',
        index: 0,
        dataGrouping: {
          enabled: false
        }
      };
    },
    seriesImpliedVolatility() {
      return {
        id: 'impliedVolatility',
        name: this.$t('white-label.widgets.optionChain.underlyingChart.series.impliedVolatility'),
        visible: this.chartTypeValue === 'hviv' ? true : false,
        type: 'line',
        yAxis: 0,
        index: 1,
        dataGrouping: {
          enabled: false
        }
      };
    },
    seriesHistoricalVolatility() {
      return {
        id: 'historicalVolatility',
        name: this.$t('white-label.widgets.optionChain.underlyingChart.series.historicalVolatility'),
        visible: this.chartTypeValue === 'hviv' ? true : false,
        type: 'line',
        yAxis: 0,
        index: 2,
        dataGrouping: {
          enabled: false
        }
      };
    },
    timespanOptions() {
      return [
        { value: '1_months', text: this.$t(`white-label.widgets.optionChain.underlyingChart.timespan.1_months`) },
        { value: '3_months', text: this.$t(`white-label.widgets.optionChain.underlyingChart.timespan.3_months`) },
        { value: '6_months', text: this.$t(`white-label.widgets.optionChain.underlyingChart.timespan.6_months`) },
        { value: '1_years', text: this.$t(`white-label.widgets.optionChain.underlyingChart.timespan.1_years`) }
      ];
    }
  },
  methods: {
    handleTimeseries() {
      if (!this.timeseries.intradayData && !this.timeseries.historicalData) {
        this.resetUnderlyingChart();
      } else {
        const timeseriesLoaded = this.timeseries.intradayData && this.timeseries.historicalData;

        if (timeseriesLoaded && this.chart) {
          this.processChartData(); // Data in series
          this.updateChartData();
        }
      }
    },
    processChartData() {
      //process historicalChartData if not yet processed
      if (!this.historicalChartData) {
        this.historicalDataSplit = {
          HIGH_1: [],
          LOW_1: [],
          CLOSE_PRC: []
        };

        this.historicalChartData = [];

        this.timeseries.historicalData.forEach(d => {
          const data = Object.assign({}, d);
          this.historicalDataSplit.HIGH_1.push(data['h.HIGH_1']);
          this.historicalDataSplit.LOW_1.push(data['h.LOW_1']);
          this.historicalDataSplit.CLOSE_PRC.push(data['h.CLOSE_PRC']);

          this.historicalChartData.push({
            x: new Date(data['h._DATE_START']).getTime(),
            y: parseFloat(data['h.CLOSE_PRC'])
          });
        });
      }

      //process intradayChartData if not yet processed
      if (this.chartTypeValue === 'hviv' && !this.intradayChartData) {
        this.intradayChartData = [];

        this.timeseries.intradayData.forEach(d => {
          const data = Object.assign({}, d);

          this.intradayChartData.push({
            x: parseFloat(data['TIME']) * 1000,
            y: parseFloat(data['CLOSE'])
          });
        });

        this.historicalVolatilityData = Volatility.calculateHistoricalVolatility(
          this.historicalDataSplit.HIGH_1,
          this.historicalDataSplit.LOW_1,
          this.historicalDataSplit.CLOSE_PRC,
          Volatility.Underlying.ClosePrice,
          9,
          9,
          252
        );
      }
    },
    updateChartData() {
      this.fromDate = this.getFromDate(this.chartTimespanValue);

      // line/mountain
      if (this.chartTypeValue === 'line' || this.chartTypeValue === 'area') {
        this.chart.get(this.seriesUnderlyingPrice.id).update(
          {
            visible: true,
            type: this.chartTypeValue,
            data: this.historicalChartData.filter(d => new Date(d.x) > this.fromDate),
            threshold: null
          },
          false
        );

        this.chart.get(this.seriesImpliedVolatility.id).update(
          {
            visible: false,
            data: []
          },
          false
        );

        this.chart.get(this.seriesHistoricalVolatility.id).update(
          {
            visible: false,
            data: []
          },
          false
        );

        this.chart.redraw();
      } else {
        // hviv
        this.chart.get(this.seriesUnderlyingPrice.id).update(
          {
            visible: false,
            data: []
          },
          false
        );

        this.chart.get(this.seriesImpliedVolatility.id).update(
          {
            visible: true,
            type: 'line',
            data: this.intradayChartData.filter(d => new Date(d.x) > this.fromDate)
          },
          false
        );

        const histVolaData = [];
        this.historicalVolatilityData.forEach((d, index) => {
          if (new Date(this.historicalChartData[index].x) > this.fromDate) {
            histVolaData.push({
              x: this.historicalChartData[index].x,
              y: Math.round(d * 100) / 100
            });
          }
        });

        this.chart.get(this.seriesHistoricalVolatility.id).update(
          {
            visible: true,
            type: 'line',
            data: histVolaData
          },
          false
        );

        this.chart.redraw();
      }
      if (this.$refs.chart) {
        this.rangeDescriptionXAxis =
          'Range:' +
          moment(new Date(this.$refs.chart.chart.xAxis[0].min)).format('dddd MMMM Do YYYY, H:mm') +
          ' to ' +
          moment(new Date(this.$refs.chart.chart.xAxis[0].max)).format('dddd MMMM Do YYYY, H:mm');
      }
    },
    resetUnderlyingChart() {
      this.historicalDataSplit = null;
      this.intradayChartData = null;
      this.historicalChartData = null;

      this.chart.get(this.seriesUnderlyingPrice.id).update({
        data: []
      });
      this.chart.get(this.seriesImpliedVolatility.id).update({
        data: []
      });
      this.chart.get(this.seriesHistoricalVolatility.id).update({
        data: []
      });
    },
    handleChartType() {
      this.processChartData();
      this.updateChartData();
    },
    onUpdateChartType(chartType) {
      this.$emit('chartType-changed', chartType);
    },
    handleChartTimespan() {
      this.updateChartData();
    },
    onUpdateTimespan(timespan) {
      this.$emit('chartTimespan-changed', timespan);
    },
    // Helpers
    getFromDate(timespan) {
      const timespanParts = timespan.split('_');
      return moment().subtract(timespanParts[0], timespanParts[1]);
    },
    getFormattedTimespan() {
      return `${this.timespanOptions.find(t => t.value === this.chartTimespan).text} [${this.$t(
        'white-label.globalLabels.day'
      )}]`;
    },
    getChartTitle() {
      return `${this.$t(`white-label.widgets.optionChain.panels.quoteChart`)} ${this.$t(
        'white-label.globalLabels.chartGraphImage'
      )}`;
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/custom/c-underlying-chart';
</style>
